import React, { useState } from 'react';
import { Image } from 'antd';
import classNames from 'classnames';
import { MessageQuestionIcon } from 'icons';
import { useTranslations } from 'next-intl';

import { useAppDispatch } from 'store';

import { setQuestionId, setTestFeedbackModal } from 'modules/global';
import { useCheckThemedTest } from 'modules/mavzuli-test';

import { MathJaxContext } from 'components';

import { IQuestion } from '../models';

interface IProps {
  test?: IQuestion;
  testName?: string;
  currentQuestion?: number;
  questionsLength?: number;
  onAnswer?: (questionId: number) => void;
}

const QuizCard: React.FC<IProps> = ({ test, testName, currentQuestion, questionsLength, onAnswer }) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const [selectedOption, setSelectedOption] = useState<number | null | undefined>(null);

  const { mutate } = useCheckThemedTest();

  const options = [
    { label: t('A_variant'), value: test?.var_a_name, id: test?.var_a_id, image: test?.var_a_image },
    { label: t('B_variant'), value: test?.var_b_name, id: test?.var_b_id, image: test?.var_b_image },
    { label: t('C_variant'), value: test?.var_c_name, id: test?.var_c_id, image: test?.var_c_image },
    { label: t('D_variant'), value: test?.var_d_name, id: test?.var_d_id, image: test?.var_d_image }
  ];

  const handleOptionSelect = (optionId?: number | null) => () => {
    if (optionId) {
      setSelectedOption(optionId);
    }

    if (optionId && test?.id && !selectedOption && !test.checked) {
      onAnswer?.(test.id);
    }

    mutate({
      testId: test?.id,
      optionId
    });
  };

  const onOpenFeedbackModal = () => {
    dispatch(setQuestionId(test?.question_id as number));
    dispatch(setTestFeedbackModal(true));
  };

  return (
    <>
      <div className="shadow-xs mb-3 rounded-xl bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {currentQuestion}/{questionsLength} • {testName}
          </div>
          <div onClick={onOpenFeedbackModal} className="flex cursor-pointer items-center gap-2 text-orange-400">
            <MessageQuestionIcon />
            <span className="text-sm">{t('xatolik_topdim')}</span>
          </div>
        </div>
        <div className="mt-3 min-w-0 overflow-x-auto overflow-y-hidden">
          <div className="text-md inline-block font-medium whitespace-pre-line">
            <MathJaxContext expression={test?.question_name} />
          </div>
        </div>
        {test?.image && (
          <div className="flex justify-center">
            <Image src={test.image} alt={test?.question_name} loading="lazy" title="Test image" />
          </div>
        )}
      </div>

      <div className="shadow-xs mb-4 rounded-xl bg-white px-4 py-3">
        <div className="space-y-3">
          {options?.map(option => {
            const userSelectedId = selectedOption ?? (test?.checked ? test[`var_${test.checked}_id`] : null);
            const isSelected = userSelectedId === option.id;

            return (
              <div
                key={option.id}
                className={classNames('rounded-xl border-2', {
                  'border-transparent': !isSelected,
                  'border-green-500 bg-green-50': isSelected
                })}
              >
                <div
                  className={classNames('flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-all', {
                    'border-gray-200': !isSelected,
                    'border-transparent': isSelected
                  })}
                  onClick={handleOptionSelect(option.id)}
                >
                  <span className="text-lg font-semibold text-gray-600">{option.label?.toLowerCase()})</span>
                  <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                    <p className="inline-block text-sm text-gray-800 whitespace-pre-line">
                      <MathJaxContext expression={option.value} />
                    </p>
                    {option?.image && (
                      <div className="mt-2 flex justify-center">
                        <Image
                          src={option.image}
                          alt={option?.value || 'Test option'}
                          loading="lazy"
                          title="Test image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default QuizCard;
